import decode from 'jwt-decode'
import apiUrl from '../utils/apiUrl'

// Types
type UserRole = string

type Permission = string

interface DecodedToken {
    username: string
    role: UserRole
    id: string
    exp: number
    jti: string
    permissions?: Permission[]
}

interface Session {
    isLoggedIn: boolean
    user: string
    role: string
    token: string
    id?: string
    jti?: string
    permissions?: Permission[]
}

interface SessionManager {
    saveSession: (tokenString: string) => DecodedToken | null
    deleteSession: () => void
    checkAdmin: () => boolean
    checkSessionAlive: (tryToRefresh?: boolean) => Promise<boolean>
    getSession: () => Session
    isFresh: () => boolean
    checkClient: () => boolean
    checkRecruit: () => boolean
}

function saveSession(tokenString: string): DecodedToken | null {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return null
        }
        const token = decode<DecodedToken>(tokenString)
        window.localStorage.setItem('user', token.username)
        window.localStorage.setItem('role', token.role)
        window.localStorage.setItem('id', token.id)
        window.localStorage.setItem('expiry', String(token.exp * 1000))
        window.localStorage.setItem('token', tokenString)
        window.localStorage.setItem('jti', token.jti)
        if (token.permissions) {
            window.localStorage.setItem(
                'permissions',
                JSON.stringify(token.permissions),
            )
        } else {
            window.localStorage.removeItem('permissions')
        }

        return token
    } catch (err) {
        return null
    }
}

function deleteSession(): void {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return
        }
        const session = getSession()
        window.localStorage.removeItem('user')
        window.localStorage.removeItem('role')
        window.localStorage.removeItem('token')
        window.localStorage.removeItem('id')
        window.localStorage.removeItem('jti')
        window.localStorage.removeItem('expiry')
        window.localStorage.removeItem('permissions')

        fetch(`//${apiUrl}/delete-refresh-token`, {
            method: 'POST',
            redirect: 'follow',
            credentials: 'include',
            body: JSON.stringify({ jti: session.jti }),
        })
            .then(() => {})
            .catch(() => {})
    } catch (err) {
        // Silently fail
    }
}

function checkAdmin(): boolean {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return false
        }
        const role = window.localStorage.getItem('role')
        if (role) {
            return role.indexOf('admin') !== -1
        }
    } catch (err) {
        return false
    }
    return false
}

function checkClient(): boolean {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return false
        }
        const role = window.localStorage.getItem('role')
        if (role) {
            return role === 'jurigo_client'
        }
    } catch (err) {
        return false
    }
    return false
}

function checkRecruit(): boolean {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return false
        }
        const role = window.localStorage.getItem('role')
        if (role) {
            return role === 'jurigo_recruit'
        }
    } catch (err) {
        return false
    }
    return false
}

async function checkSessionAlive(
    tryToRefresh: boolean = false,
): Promise<boolean> {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return false
        }
        const expStr = window.localStorage.getItem('expiry')

        if (expStr) {
            const exp = parseInt(expStr, 10)
            // 1000 = 1 second
            // if the token has expired for less than 48 hours
            // we'll be able to refresh the session with the refresh token,
            // so return true
            const now = Date.now()
            const expiryPlus47Hours = exp + 47 * 3600 * 1000
            const expiryMinus5Seconds = exp - 5 * 1000
            if (now < expiryMinus5Seconds) {
                return true
            }
            if (now > expiryMinus5Seconds && expiryPlus47Hours > now) {
                // if session is about to expired (or about to in 5s) and
                // session has not been expired for more than 47 hours,
                // try to refresh
                if (tryToRefresh) {
                    const refreshed = await refresh()
                    if (refreshed) {
                        return true
                    } else {
                        return false
                    }
                }
            }
        }
        return false
    } catch (err) {
        return false
    }
}

function getSession(): Session {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return {
                isLoggedIn: false,
                user: '',
                role: '',
                token: '',
            }
        }
        if (window.localStorage.getItem('jti')) {
            return {
                isLoggedIn: true,
                user: window.localStorage.getItem('user') || '',
                role: window.localStorage.getItem('role') || '',
                token: window.localStorage.getItem('token') || '',
                id: window.localStorage.getItem('id') || undefined,
                jti: window.localStorage.getItem('jti') || undefined,
            }
        }
    } catch (err) {
        return {
            isLoggedIn: false,
            user: '',
            role: '',
            token: '',
        }
    }
    return {
        isLoggedIn: false,
        user: '',
        role: '',
        token: '',
    }
}

// Module-level variable to track in-flight refresh request
// This prevents race conditions when multiple requests try to refresh simultaneously
let refreshPromise: Promise<Session | null> | null = null

function refresh(): Promise<Session | null> {
    // If a refresh is already in progress, return the existing promise
    // This ensures all concurrent 401 responses wait for the same refresh
    if (refreshPromise) {
        return refreshPromise
    }

    const currSession = getSession()

    refreshPromise = new Promise<Session | null>((resolve, reject) => {
        fetch(`//${apiUrl}/refresh-token`, {
            method: 'POST',
            redirect: 'follow',
            credentials: 'include',
            headers: {
                Authorization: currSession.token,
            },
            body: JSON.stringify({ jti: currSession.jti }),
        })
            .then((res) => {
                if (res.status === 200) {
                    res.json().then((json: { token: string }) => {
                        const newSession = saveSession(json.token)
                        resolve({
                            ...currSession,
                            ...newSession,
                            isLoggedIn: true,
                        } as Session)
                    })
                } else {
                    deleteSession()
                    resolve(null)
                }
            })
            .catch((err) => {
                reject(err)
            })
    }).finally(() => {
        // Clear the promise so future refreshes can proceed
        refreshPromise = null
    })

    return refreshPromise
}

function sessionIsFresh(): boolean {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return false
        }
        const expStr = window.localStorage.getItem('expiry')

        if (expStr) {
            const exp = parseInt(expStr, 10)
            const now = Date.now()
            if (now < exp) {
                return true
            }
            console.log('false now is bigger than expiry')
        }
        return false
    } catch (err) {
        return false
    }
}

const sessionManager: SessionManager = {
    saveSession,
    deleteSession,
    checkAdmin,
    checkSessionAlive,
    getSession,
    isFresh: sessionIsFresh,
    checkClient,
    checkRecruit,
}

export default sessionManager

export const refreshSession = refresh

// Export types for use in other files
export type { Session, Permission, UserRole, DecodedToken, SessionManager }
